import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cacheQueryData, getCachedQueryData } from '../utils/scoreQueue'
import { fetchMyFollows, fetchMyFollowers, followPlayer, unfollowPlayer, fetchRecentCoPlayers } from '../api/follows'

/**
 * Cached list of players the current user follows.
 */
export function useMyFollows() {
  return useQuery({
    queryKey: ['my-follows'],
    queryFn: async () => {
      const data = await fetchMyFollows()
      cacheQueryData('my-follows', data)
      return data
    },
    staleTime: 60_000,
    initialData: () => getCachedQueryData('my-follows'),
    initialDataUpdatedAt: 0,
  })
}

/**
 * Cached list of players who follow the current user.
 */
export function useMyFollowers() {
  return useQuery({
    queryKey: ['my-followers'],
    queryFn: async () => {
      const data = await fetchMyFollowers()
      cacheQueryData('my-followers', data)
      return data
    },
    staleTime: 60_000,
    initialData: () => getCachedQueryData('my-followers'),
    initialDataUpdatedAt: 0,
  })
}

/**
 * Derived check — is a specific player followed? Uses the cached follows list.
 */
export function useIsFollowing(playerId) {
  const { data: follows } = useMyFollows()
  if (!follows || !playerId) return false
  return follows.some((p) => p.id === playerId)
}

/**
 * Mutation to follow a player.
 */
export function useFollowPlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: followPlayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-follows'] })
      queryClient.invalidateQueries({ queryKey: ['my-followers'] })
      queryClient.invalidateQueries({ queryKey: ['recent-co-players'] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
    },
  })
}

/**
 * Mutation to unfollow a player.
 */
export function useUnfollowPlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: unfollowPlayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-follows'] })
      queryClient.invalidateQueries({ queryKey: ['my-followers'] })
      queryClient.invalidateQueries({ queryKey: ['recent-co-players'] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
    },
  })
}

/**
 * Players you've shared a match with but don't currently follow.
 */
export function useRecentCoPlayers() {
  return useQuery({
    queryKey: ['recent-co-players'],
    queryFn: fetchRecentCoPlayers,
    staleTime: 60_000,
  })
}
